import * as Sentry from '@sentry/react'
import { MinimalVersion, IMicrofront } from '../../interfaces/MFE/IMicrofront'
import { Aura } from 'auragin'
import INITIAL_MODULES from '../../../../config/modules/initial-modules.json'
import blobToBase64 from '../blobToBase64'
import logError from '../../consts/log-error'
import readModulesFromDevice from './read-modules'
import { filesystem } from '../init'
import { IDiscoveryResponse } from '../../interfaces/MFE/IDiscoveryAPI'

export async function validateDownloadedModules (microfrontendsResponse: IDiscoveryResponse): Promise<any> {
  const modulesFromDevice = await readModulesFromDevice()
  const initialApplicationDiscovery = await fetch('/assets/constants/default-discovery.json').then(async res => await res.json()).then(res => res.microFrontends)

  Sentry.addBreadcrumb({
    category: 'init.modules',
    message: `[validateModules] Starting - ${modulesFromDevice.length} modules on device, ${microfrontendsResponse.microFrontends.length} discovered`,
    level: 'info',
    data: {
      modulesOnDevice: modulesFromDevice.map(m => `${m.mfe_id}@${m.version}`),
      discoveredCount: microfrontendsResponse.microFrontends.length
    }
  })

  const deletePromises = modulesFromDevice
    .filter(downloadedMicrofront => (initialApplicationDiscovery?.find((discoveredMicrofront: IMicrofront) => downloadedMicrofront.mfe_id === discoveredMicrofront.mfe_id)) == null)
    .map(async (localModule) => {
      Sentry.addBreadcrumb({
        category: 'init.modules',
        message: `[validateModules] Removing orphan module: ${localModule.mfe_id}@${localModule.version}`,
        level: 'warning'
      })
      return await filesystem?.removeDir(filesystem.getPath('modules', { mfe_id: localModule.mfe_id, mfe_version: localModule.name })).catch(console.error)
    })

  return await Promise.all(
    microfrontendsResponse.microFrontends
      .filter(module => INITIAL_MODULES.core.includes(module.mfe_id) || INITIAL_MODULES.baseComponents.includes(module.mfe_id) || INITIAL_MODULES.features.includes(module.mfe_id))
      .map(async (module) => await checkInitialModule(module, modulesFromDevice, initialApplicationDiscovery))
      .concat(deletePromises)
  )
}

async function checkInitialModule (
  module: IMicrofront, 
  modulesFromDevice: MinimalVersion[], 
  initialApplicationDiscovery: IMicrofront[]
): Promise<void> {

  if (filesystem == null) return

  const versionsInDevice = modulesFromDevice.filter(m => m.mfe_id === module.mfe_id)
  const discoveryVersion = module.version
  const hasDiscoveryVersion = versionsInDevice.some(m => m.version === discoveryVersion)
  
  if (hasDiscoveryVersion) {
    // CASO FELIZ: la versión del discovery ya existe en el dispositivo, eliminar las demás versiones (si es que hay)
    removeOtherVersions(module.mfe_id, discoveryVersion, versionsInDevice)
    return
  }

  // A partir de aca ya asumo que no está la del discovery
  if (versionsInDevice.length > 0) {
    // Check if a bundled version exists in initialApplicationDiscovery
    const initialModule = initialApplicationDiscovery.find(m => m.mfe_id === module.mfe_id)
    if (initialModule != null) {
      // Bundled version available: remove ALL old versions and extract the bundled zip
      removeOtherVersions(module.mfe_id, '', versionsInDevice)
      const targetVersion = initialModule.version
      const path = filesystem.getPath('modules', { filename: `${module.mfe_id}.zip`, mfe_id: module.mfe_id, mfe_version: targetVersion })

      Sentry.addBreadcrumb({
        category: 'init.modules',
        message: `[validateModules] Re-extracting bundled module (version mismatch): ${module.mfe_id}@${targetVersion}`,
        level: 'info',
        data: { mfe_id: module.mfe_id, targetVersion, existingVersions: versionsInDevice.map(v => v.version) }
      })

      await fetch(`/mobile/modules/${module.mfe_id}.zip`)
        .then(async res => await res.blob())
        .then(async blob => await blobToBase64(blob))
        .then(async base64 => await Aura.unZipLocal({ fullPath: path, base64 }))
        .catch((err) => {
          Sentry.captureException(
            err instanceof Error ? err : new Error(`[validateModules] Failed to re-extract bundled module ${module.mfe_id}`),
            {
              tags: { init_phase: 'validate_modules', mfe_id: module.mfe_id, mfe_version: targetVersion },
              extra: { path, targetVersion, scenario: 'version_mismatch_re_extract' }
            }
          )
          logError(err)
        })
      return
    }

    // No bundled version: keep the most recent local version (existing behavior)
    const sorted = [...versionsInDevice].sort((a, b) => compareVersions(b.version, a.version))
    const latest = sorted[0]
    removeOtherVersions(module.mfe_id, latest.version, versionsInDevice)
    return
  }

  // No existe ninguna versión en el dispositivo: descomprimir la versión inicial bundleada
  const initialModule = initialApplicationDiscovery.find(m => m.mfe_id === module.mfe_id)
  const targetVersion = initialModule?.version ?? module.version
  const path = filesystem.getPath('modules', { filename: `${module.mfe_id}.zip`, mfe_id: module.mfe_id, mfe_version: targetVersion })

  Sentry.addBreadcrumb({
    category: 'init.modules',
    message: `[validateModules] Extracting bundled module: ${module.mfe_id}@${targetVersion}`,
    level: 'info',
    data: { mfe_id: module.mfe_id, targetVersion, path }
  })

  await fetch(`/mobile/modules/${module.mfe_id}.zip`)
    .then(async res => await res.blob())
    .then(async blob => await blobToBase64(blob))
    .then(async base64 => await Aura.unZipLocal({ fullPath: path, base64 }))
    .catch((err) => {
      Sentry.captureException(
        err instanceof Error ? err : new Error(`[validateModules] Failed to extract bundled module ${module.mfe_id}`),
        {
          tags: { init_phase: 'validate_modules', mfe_id: module.mfe_id, mfe_version: targetVersion },
          extra: { path, targetVersion, scenario: 'no_version_on_device' }
        }
      )
      logError(err)
    })
}

function removeOtherVersions (mfeId: string, keepVersion: string, versionsInDevice: MinimalVersion[]): void {
  versionsInDevice
    .filter(m => keepVersion === '' || m.version !== keepVersion)
    .forEach(m => {
      console.warn(`Removing module ${mfeId} version ${m.version} from device, keeping version ${keepVersion}`)
      filesystem?.removeDir(filesystem.getPath('modules', { mfe_id: mfeId, mfe_version: m.version })).catch(console.error)
    })
}

function compareVersions (a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}
